import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ stats, onSignUp, onLogin }) => {
  return (
    <section className="relative overflow-hidden py-16 md:py-24">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-coral-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Headline & CTAs */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-orange-100 text-black px-4 py-2 rounded-full mb-6 text-sm font-medium">
              <Icon name="Sparkles" size={16} />
              <span>Join 15,000+ changemakers</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight">
              Make Giving{' '}
              <span className="text-black font-bold">
                Personal
              </span>
              ,{' '}
              <span className="text-black font-bold">
                Impactful
              </span>
              , and{' '}
              <span className="text-black font-bold">
                Fun
              </span>
            </h1>

            <p className="text-xl text-black mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Connect with causes that matter to you, track your impact in real-time, and join a community of givers creating positive change across the globe.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Button
                size="lg"
                onClick={onSignUp}
                className="bg-gradient-to-r from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 text-black px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                iconName="ArrowRight"
                iconPosition="right"
              >
                Start Your Journey
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onLogin}
                className="border-2 border-coral-300 text-coral-700 hover:bg-coral-50 px-8 py-4 text-lg"
                iconName="LogIn"
                iconPosition="left"
              >
                Sign In
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-black">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-teal-600" />
                <span>100% Secure</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={16} className="text-teal-600" />
                <span>Verified Nonprofits</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} className="text-teal-600" />
                <span>Tax Deductible</span>
              </div>
            </div>
          </div>

          {/* Right Column - Stats Cards */}
          <div className="grid grid-cols-2 gap-4 lg:gap-6">
            {/* Total Donated */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-orange-100">
              <div className="w-12 h-12 bg-gradient-to-br from-coral-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                <Icon name="DollarSign" size={24} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-black mb-1">
                ${(stats?.totalDonated / 1000000)?.toFixed(1)}M+
              </div>
              <div className="text-sm text-black">Total Donated</div>
            </div>

            {/* Active Donors */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-orange-100">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                <Icon name="Users" size={24} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-black mb-1">
                {(stats?.activeDonors / 1000)?.toFixed(1)}K+
              </div>
              <div className="text-sm text-black">Active Donors</div>
            </div>

            {/* Causes Supported */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-teal-100">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                <Icon name="Heart" size={24} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-black mb-1">
                {stats?.causesSupported}+
              </div>
              <div className="text-sm text-black">Causes Supported</div>
            </div>

            {/* Nonprofits Partnered */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-teal-100">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <Icon name="Building" size={24} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-black mb-1">
                {stats?.nonprofitsPartnered}+
              </div>
              <div className="text-sm text-black">Nonprofits Partnered</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;